import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Save, Upload, X } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectHomeTabForm = ({
  homeData,
  setHomeData,
  onSave,
  isProject,
  setActiveTab,
  projectId,
}) => {
  const cardCls =
    "bg-white rounded-xl shadow p-8 mb-10";
  const labelCls =
    "block text-sm font-medium text-gray-700 mb-1";
  const inputCls =
    "w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none";

  const skipAutosave = useRef(false);
  const [draftId, setDraftId] = useState(null);
  const saveTimer = useRef(null);
  const isInitialLoad = useRef(true);

  // Load existing draft on mount
  useEffect(() => {
    const loadDraft = async () => {
      const storedDraftId = localStorage.getItem("draftId");
      if (!storedDraftId) {
        isInitialLoad.current = false;
        return;
      }

      const token = localStorage.getItem("token");

      try {
        const res = await axios.get(
          `${API_BASE_URL}/admin/get-draft-project/${storedDraftId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.draft) {
          setHomeData(res.data.draft.draft_data);
          setDraftId(res.data.draft.draft_id);
        }
      } catch (err) {
        console.error("Error loading draft:", err);
      } finally {
        isInitialLoad.current = false;
      }
    };

    if (!isProject) loadDraft();
    else isInitialLoad.current = false;
  }, [isProject, setHomeData]);

  // Autosave
  const autosaveDraft = async () => {
    if (!homeData.project_name || homeData.project_name.trim() === "") {
      return;
    }

    const token = localStorage.getItem("token");
    const payload = {
      draft_id: draftId,
      project_id: projectId,
      formData: homeData,
    };

    try {
      const res = await axios.post(
        `${API_BASE_URL}/admin/save-draft-project`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!draftId && res.data.draft_id) {
        setDraftId(res.data.draft_id);
        localStorage.setItem("draftId", res.data.draft_id);
      }
    } catch (err) {
      console.error("Error autosaving draft:", err);
    }
  };

  // Debounced autosave when homeData changes
  useEffect(() => {
    if (!homeData || !homeData.project_name?.trim()) return;

    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      autosaveDraft();
    }, 800);

    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHomeData((prev) => ({ ...prev, [name]: value }));
  };

  // Main image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("media", file);

    axios
      .post(`${API_BASE_URL}/admin/upload-media`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        const uploadedUrl = res.data?.data?.files?.[0]?.url;
        if (uploadedUrl) {
          setHomeData((prev) => ({
            ...prev,
            project_image: uploadedUrl,
          }));
        }
      })
      .catch((err) => {
        console.error("upload-media error", err);
      });
  };

  const removeImage = () => {
    setHomeData((prev) => ({ ...prev, project_image: "" }));
  };

  // ✅ Brochure upload (PDF)
  const handleBrochureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Please upload a PDF file for the brochure.");
      return;
    }

    const formData = new FormData();
    formData.append("media", file);

    axios
      .post(`${API_BASE_URL}/admin/upload-media`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        const uploadedUrl = res.data?.data?.files?.[0]?.url;
        if (uploadedUrl) {
          setHomeData((prev) => ({
            ...prev,
            brochure: uploadedUrl,
          }));
        }
      })
      .catch((err) => {
        console.error("brochure upload error", err);
        alert("Brochure upload failed. Please try again.");
      });
  };

  const removeBrochure = () => {
    setHomeData((prev) => ({ ...prev, brochure: "" }));
  };

  const clearForm = () => {
    skipAutosave.current = true;

    setHomeData({
      project_name: "",
      project_status: "",
      project_description: "",
      project_rera: "",
      project_extent: "",
      project_location: "",
      project_badge_location: "",
      project_type: "",
      project_price: "",
      project_complete_date: "",
      approval: "",
      hmda: "",
      features: "",
      brochure: "",
      project_image: "",
      gallery_description: "",
      gallery_images: [],
      // project_location: [],
      project_plan_images: [],
      project_amenities: [],
    });

    localStorage.setItem("draftId", "");
  };

  return (
    <section className={cardCls}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">
          Home Tab Content
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={clearForm}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
          >
            <X size={18} /> Clear
          </button>

          <button
            onClick={() => setActiveTab("gallery")}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Next
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Project Name & Status */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Project Name</label>
            <input
              name="project_name"
              value={homeData.project_name}
              onChange={handleChange}
              className={inputCls}
              placeholder="e.g., HPR AVANI 2"
            />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select
              name="project_status"
              value={homeData.project_status}
              onChange={handleChange}
              className={inputCls}
            >
              <option value="">Select status</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className={labelCls}>Description</label>
          <textarea
            name="project_description"
            value={homeData.project_description}
            onChange={handleChange}
            className={inputCls}
            placeholder="e.g., Premium HMDA Villa Plots"
            rows={5}
          />
        </div>

        {/* Project Information */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Project Information
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>RERA Number</label>
              <input
                name="project_rera"
                value={homeData.project_rera}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., RERA:P0240012345"
              />
            </div>
            <div>
              <label className={labelCls}>Extent</label>
              <input
                name="project_extent"
                value={homeData.project_extent}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., 24 Acres"
              />
            </div>
           
            <div>
              <label className={labelCls}>Badge Location</label>
              <input
                name="project_badge_location"
                value={homeData.project_badge_location}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., Kandi"
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Additional Details
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Project Type</label>
              <input
                name="project_type"
                value={homeData.project_type}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., Villa Plots"
              />
            </div>
            <div>
              <label className={labelCls}>Project Status</label>
              <input
                name="project_price"
                value={homeData.project_price}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., Price on request"
              />
            </div>
            <div>
              <label className={labelCls}>Completion Date</label>
              <input
                name="project_complete_date"
                value={homeData.project_complete_date}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., 2026"
              />
            </div>
            <div>
              <label className={labelCls}>Approval</label>
              <input
                name="approval"
                value={homeData.approval}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., HMDA Approved"
              />
            </div>
            <div>
              <label className={labelCls}>HMDA</label>
              <input
                name="hmda"
                value={homeData.hmda}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., Yes"
              />
            </div>
            <div>
              <label className={labelCls}>Features</label>
              <input
                name="features"
                value={homeData.features}
                onChange={handleChange}
                className={inputCls}
                placeholder="e.g., BT Roads, OHT, Street Lights"
              />
            </div>
          </div>
        </div>

        {/* ✅ Brochure Upload */}
        <div className="border-t pt-6">
          <label className={labelCls}>Project Brochure (PDF)</label>

          {homeData.brochure ? (
            <div className="flex items-center gap-4 bg-gray-50 border rounded-lg px-4 py-3">
              <span className="text-sm text-gray-700 truncate">
                {homeData.brochure}
              </span>
              <a
                href={homeData.brochure}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 text-sm underline"
              >
                View
              </a>
              <button
                type="button"
                onClick={removeBrochure}
                className="ml-auto bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-lg flex items-center gap-1"
              >
                <X size={14} /> Remove
              </button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                id="brochure-upload"
                accept="application/pdf"
                onChange={handleBrochureUpload}
                className="hidden"
              />
              <label
                htmlFor="brochure-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload size={32} className="text-gray-400" />
                <span className="text-gray-700 text-sm">
                  Click to upload brochure (PDF)
                </span>
                <span className="text-xs text-gray-400">
                  Max size as per server limit
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Main Project Image */}
        <div className="border-t pt-6">
          <label className={labelCls}>Main Project Image</label>
          {homeData.project_image ? (
            <div className="relative inline-block">
              <img
                src={homeData.project_image}
                alt="Project preview"
                className="w-full max-w-md h-64 object-cover rounded-lg border"
              />
              <button
                onClick={removeImage}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                id="home-main-image"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <label
                htmlFor="home-main-image"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <Upload size={48} className="text-gray-400" />
                <span className="text-gray-600">
                  Click to upload an Image
                </span>
                <span className="text-sm text-gray-400">
               Recommended image size: 1200 × 800 px (3:2), JPG/PNG, under 300 KB
                </span>
              </label>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProjectHomeTabForm;
